// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// One drawer implementation, shared by every drill-down.
//
// THE DISMISSAL CONTRACT — all four exits, always, and none of them optional:
//   1. a ✕ with a real 44x44 hit area,
//   2. a tap anywhere on the scrim,
//   3. Escape from anywhere on the page,
//   4. and the panel must actually be hidden afterwards, in COMPUTED STYLE.
//
// Two things that have shipped broken before and are handled deliberately here:
//   * The SCRIM listener is `pointerdown` — it covers mouse, touch and pen in
//     one handler, and a scrim is never focusable so nothing is lost. The
//     BUTTON listener is `click`, because a <button> synthesises a click from
//     Enter and Space and `pointerdown` alone would make the ✕ dead to every
//     keyboard and screen-reader user. One event family per control either
//     way: attaching both double-fires on a phone, which closes and instantly
//     reopens the overlay and reads as "the close button does nothing".
//   * The open state is a CSS @keyframes entrance whose RESTING state is
//     on-screen, not a class that triggers a transition from translateX(100%).
//     A transition kicked off inside requestAnimationFrame is throttled in a
//     backgrounded tab, which leaves the panel parked off-screen forever.
//
// The panel is DETACHED from the DOM when closed rather than left in place at
// translateX(100%): an off-screen fixed box is still a real box on iOS Safari
// and silently gives the page a horizontal scroll that `overflow-x: clip`
// neither prevents nor lets `scrollWidth` detect.

let host: HTMLDivElement | null = null;
let scrim: HTMLDivElement | null = null;
let panel: HTMLElement | null = null;
let lastFocus: HTMLElement | null = null;
let onCloseHook: (() => void) | null = null;

function build(): void {
  if (host) return;
  host = document.createElement('div');
  host.className = 'drawer-host';

  scrim = document.createElement('div');
  scrim.className = 'drawer-scrim';
  scrim.setAttribute('aria-hidden', 'true');

  panel = document.createElement('aside');
  panel.className = 'drawer';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('aria-label', 'Details');

  host.append(scrim, panel);

  // pointerdown, not click: it fires for mouse, touch and pen alike, and it
  // still fires in the places a synthetic click would not.
  scrim.addEventListener('pointerdown', (e) => { e.preventDefault(); closeDrawer(); });
}

export function isDrawerOpen(): boolean {
  return Boolean(host?.isConnected);
}

/** Focus trap + Escape. Bound once, for the lifetime of the page. */
function onKeydown(e: KeyboardEvent): void {
  if (!isDrawerOpen()) return;
  if (e.key === 'Escape') { e.preventDefault(); closeDrawer(); return; }
  if (e.key !== 'Tab' || !panel) return;
  const focusable = panel.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
}

export function initDrawer(): void {
  build();
  document.addEventListener('keydown', onKeydown);
}

export function openDrawer(title: string, bodyHtml: string, onClose?: () => void): void {
  build();
  if (!host || !panel) return;
  lastFocus = document.activeElement as HTMLElement | null;
  onCloseHook = onClose ?? null;

  panel.innerHTML = `
    <header class="drawer-head">
      <h2>${title}</h2>
      <button type="button" class="drawer-close" aria-label="Close details">✕</button>
    </header>
    <div class="drawer-body">${bodyHtml}</div>`;

  // `click`, NOT `pointerdown`: a <button> synthesises a click from Enter and
  // Space, so pointerdown alone leaves the ✕ completely dead to keyboard and
  // screen-reader users while looking fine to a mouse. Touch and pen still
  // produce a click, so nothing is lost. Never attach both to one control —
  // that double-fires on a phone and instantly reopens what it just closed.
  panel.querySelector<HTMLButtonElement>('.drawer-close')?.addEventListener('click', (e) => {
    e.preventDefault();
    closeDrawer();
  });

  document.body.appendChild(host);
  document.body.classList.add('drawer-open');
  panel.querySelector<HTMLButtonElement>('.drawer-close')?.focus();
}

export function closeDrawer(): void {
  if (!host || !host.isConnected) return;
  host.remove(); // detach, do not merely translate off-screen
  document.body.classList.remove('drawer-open');
  const hook = onCloseHook;
  onCloseHook = null;
  if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
  lastFocus = null;
  if (hook) hook();
}
